// 261010 P3-1 (luồng Sky) — mục "Storyboard" của MỘT thẻ video: tấm ảnh 9:16 + bảng khung sửa được + soạn/gen. Logic thuần ở storyboardUtils.ts.
// Dữ liệu tải MỘT LẦN mỗi plan ở EventPlanTab (khuôn PositionMapPanel) rồi truyền xuống qua `ctx`; việc nền dùng lại `jobs`/`failures` mà tab đã poll.
// Chỉ render khi plan sky (EventCard kiểm `isSkyPlan`) — plan khác gọi route storyboard sẽ 409 NOT_SKY_PLAN.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Image, Input, InputNumber, Popconfirm, Select, Space, Spin, Tag, Tooltip, message } from 'antd';
import { filmApi, type FilmBgStarted } from '../../services/api/film';
import type { FilmEvent, FilmFailedJob, FilmRunningJob, Storyboard, StoryboardFrame } from '../../types/film';
import type { AutoPrompt } from '../../types/filmWorkflow';
import { actualCostLabel } from './positionMapUtils';
import PromptChipEditor from './PromptChipEditor'; // 261010 P9D #29
import { storyboardChipsOf } from './promptSurfaceUtils';
import { combineGate, confirmContent, genButtonLabel, spentLabel, workflowActionError, type ActionError, type ActionGate } from './workflowActionPolicy'; // 261010 P2b-1
import {
  ACTION_MAX, ANGLE_OPTIONS, MOVE_OPTIONS, SHOT_SIZE_OPTIONS, WHO_MAX,
  canGenerateSheet, canSaveFrames, cloneFrames, composeConfirm, driftLines, framesEqual, framesPayload, isFramesChanged, isStoryboardEdited,
  mapOptionsFor, patchFrame, refsWarningLines, setFrameGroup, setGroupTime, splitRefsWarnings, storyboardCostLabel, storyboardErrorText, storyboardFailureOf,
  storyboardApprovalErrorText, storyboardApprovalView, storyboardJobName, storyboardJobOf, storyboardTags, validateDraft,
} from './storyboardUtils';

/** Phần plan-level mà EventPlanTab truyền xuống mọi thẻ video sky. */
export interface StoryboardCtx {
  /** eventId → storyboard (event chưa soạn không có khoá). */
  byEvent: Record<string, Storyboard>;
  /** Đã nạp xong ít nhất một lần (trước đó hiện khung chờ, không hiện "chưa có storyboard" sớm). */
  loaded: boolean;
  loading: boolean;
  loadError: string | null;
  /** Liên kết video→giai đoạn→sơ đồ + tên sơ đồ (nguồn: GET position-maps). `null` = nạp lỗi ⇒ ô sơ đồ chỉ giữ giá trị đã lưu. */
  pos: { links: Array<{ eventId: string; phaseOrder: number | null; positionMapId: string }>; maps: Array<{ id: string; name: string }> } | null;
  jobs: FilmRunningJob[];
  failures: FilmFailedJob[];
  onReload: () => Promise<void>;
  /** Hỏi `GET /plans/:id/jobs` NGAY (sau 202); phần poll định kỳ do EventPlanTab lo. */
  onJobsRefresh: () => Promise<{ jobs: FilmRunningJob[]; failures?: FilmFailedJob[] } | undefined>;
}

const OPEN_KEY = (eventId: string) => `film.storyboard.open.${eventId}`;
const readOpen = (eventId: string): boolean => { try { return localStorage.getItem(OPEN_KEY(eventId)) === '1'; } catch { return false; } };
const writeOpen = (eventId: string, v: boolean) => { try { localStorage.setItem(OPEN_KEY(eventId), v ? '1' : '0'); } catch { /* private mode: chấp nhận quên */ } };

const fmtS = (n: number): string => String(Math.round(Number(n) * 100) / 100).replace('.', ',');
const toOptions = (opts: Array<{ value: string; label: string; hint?: string }>) => opts.map((o) => ({ value: o.value, label: o.label, title: o.hint }));
const GROUP_OPTIONS = [1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }));
const TH: React.CSSProperties = { padding: '4px 6px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' };
const TD: React.CSSProperties = { padding: '4px 6px', verticalAlign: 'top' };

/** 261010 P2b-1 — prop tuỳ chọn cho khung mới. Không truyền gì ⇒ UI cũ y nguyên từng chữ. */
export interface StoryboardBlockWorkflowProps {
  /** 261010 P9D #29 (I1) — prompt tấm hiện bằng CHIP chỉ đọc (ảnh trong câu), không ô Lưu; sửa storyboard qua khung/shot. Không truyền ⇒ chữ thô như cũ. */
  chipPrompt?: boolean;
  onDirtyChange?:(dirty:boolean)=>void;
  /** ẨN giá ở nhãn nút, dòng "đã tốn", popup gen và dòng "(vài cent)" của popup soạn; hộp xác nhận gen đổi sang bản không giá. */
  hideCost?: boolean;
  /** Luôn mở (không nhớ trạng thái gập ở localStorage, không hiện nút Thu gọn). */
  alwaysOpen?: boolean;
  /** Mọi lỗi nghiệp vụ của thao tác (đã dịch ra câu tiếng Việt + loại) — khung mới hiện trong khối bước, không chỉ toast. */
  onActionError?: (e: ActionError) => void;
  /** 261010 P2c-2 — duyệt xong: báo `autoPrompt` của response (móc "duyệt ⇒ tự dựng prompt"). Không truyền ⇒ UI cũ y nguyên (vẫn chỉ toast + nạp lại). */
  onApproved?: (autoPrompt: AutoPrompt | undefined) => void;
  /** 261030 B2 (khung 4 bước) — duyệt tấm KHÔNG móc "duyệt ⇒ tự dựng prompt" (prompt thuộc bước ④). Không truyền ⇒ y như cũ. */
  noAutoPrompt?: boolean;
  /** 261030 B2 — chỉ khi `hideCost`: dòng tiền thêm vào hộp xác nhận tạo tấm / soạn khung. */
  genCostNote?: string;
  composeCostNote?: string;
  /** Cổng từ `workflowActionPolicy` (chỉ thêm ràng buộc ẩn/tắt). */
  policy?: { compose?: ActionGate; save?: ActionGate; generate?: ActionGate; approve?: ActionGate; unapprove?: ActionGate };
}

const StoryboardBlock: React.FC<{ ev: FilmEvent; ctx: StoryboardCtx } & StoryboardBlockWorkflowProps> = ({ ev, ctx, hideCost, alwaysOpen, onActionError, onApproved, noAutoPrompt, genCostNote, composeCostNote, policy, chipPrompt,onDirtyChange }) => {
  const sb: Storyboard | undefined = ctx.byEvent[ev.id];
  const [open, setOpen] = useState(() => !!alwaysOpen || readOpen(ev.id));
  const [draft, setDraft] = useState<StoryboardFrame[]>(() => (sb ? cloneFrames(sb.frames) : []));
  const [sending, setSending] = useState<null | 'compose' | 'generate' | 'save' | 'approve' | 'unapprove'>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);

  // Bản nháp chỉ reset khi khung ĐÃ LƯU đổi (phiên bản/id khác), KHÔNG reset mỗi lần nạp lại danh sách (sẽ mất ô người đang sửa dở).
  const serverVersion = sb?.framesVersion;
  const serverId = sb?.id;
  const sbRef = useRef(sb);
  sbRef.current = sb;
  useEffect(() => { setDraft(sbRef.current ? cloneFrames(sbRef.current.frames) : []); }, [serverId, serverVersion]);

  const dirty = !!sb && !framesEqual(draft, sb.frames);
  useEffect(()=>{onDirtyChange?.(dirty);},[dirty,onDirtyChange]);
  useEffect(()=>()=>onDirtyChange?.(false),[onDirtyChange]);
  const job = storyboardJobOf(ctx.jobs, ev.id);
  const failure = storyboardFailureOf(ctx.failures, ev.id, dismissed);
  const running = !!job || sending !== null;
  const validation = useMemo(() => (dirty ? validateDraft(draft, { genDurationS: ev.genDurationS }) : { ok: true, errors: [] as string[] }), [dirty, draft, ev.genDurationS]);
  const tags = storyboardTags(sb, { job, error: failure ? `${storyboardJobName(failure.phase)}: ${failure.note}` : null, dirty });
  const { blocking, notes } = splitRefsWarnings(sb?.refsWarnings);
  const hasShots = (ev.shots || []).length > 0;
  const genGate = (() => { const g = combineGate(canGenerateSheet(sb, { running, dirty }), policy?.generate); return { ok: g.ok, reason: g.reason, visible: g.visible }; })();
  const saveGate = (() => { const g = combineGate(canSaveFrames({ dirty, running: !!job || sending === 'compose' || sending === 'generate' }), policy?.save); return { ok: g.ok, reason: g.reason }; })();
  const cc = composeConfirm(sb?.authorStatus, dirty);
  const cost = storyboardCostLabel(sb?.estimatedCostUsd);
  const spent = spentLabel(actualCostLabel(sb?.sheetCostUsd), hideCost);
  const chipView = useMemo(() => (chipPrompt ? storyboardChipsOf(sb?.renderedPrompt || '', sb?.refsPreview || []) : { text: '', choices: [] }), [chipPrompt, sb?.renderedPrompt, sb?.refsPreview]);
  const hasSheet = !!sb?.sheetUrl;
  const approval = sb ? storyboardApprovalView(sb) : null;
  const drift = sb ? driftLines(sb.drift || []) : [];
  const toggle = () => { const v = !open; setOpen(v); if (!alwaysOpen) writeOpen(ev.id, v); };
  /** Báo lỗi: toast như cũ + (khung mới) hiện trong khối bước. */
  const fail = (e: any, what: string, toast: string) => { message.error(toast); onActionError?.(workflowActionError(e, what)); };

  /** Sau 202: hỏi jobs NGAY; nếu việc đã xong/chết trước khi thấy nó chạy thì nạp lại luôn. */
  const afterStarted = async (started: FilmBgStarted) => {
    const r = await ctx.onJobsRefresh();
    if (started.jobId && r && !r.jobs.some((j) => j.id === started.jobId)) await ctx.onReload();
  };

  const doCompose = async (confirmReplace: boolean) => {
    setSending('compose');
    try {
      const started = await filmApi.composeStoryboard(ev.id, confirmReplace);
      message.info('AI đang soạn khung ở nền.');
      await afterStarted(started);
    } catch (e: any) {
      fail(e, 'Soạn khung', storyboardErrorText(e, 'Soạn khung'));
      // Trạng thái hiển thị đã cũ (ai đó vừa sửa / vừa xoá) ⇒ nạp lại để lần bấm sau hỏi xác nhận đúng.
      if (isStoryboardEdited(e) || e?.response?.data?.code === 'NOT_FOUND') await ctx.onReload();
    } finally { setSending(null); }
  };

  const doGenerate = async () => {
    setSending('generate');
    try {
      const started = await filmApi.generateStoryboard(ev.id);
      message.info('Đang gen tấm storyboard ở nền.');
      await afterStarted(started);
    } catch (e: any) {
      fail(e, hideCost ? 'Tạo tấm' : 'Gen tấm', storyboardErrorText(e, 'Gen tấm'));
      const code = e?.response?.data?.code;
      if (code === 'NO_COMPOSITION' || code === 'NO_PLATE' || code === 'TOO_MANY_CHARACTERS' || code === 'NOT_FOUND') await ctx.onReload();
    } finally { setSending(null); }
  };

  // 261010 P4-1 — cổng duyệt (chỉ người; chỉ bản đã duyệt mới vào video). Khoá FILM_STORYBOARD: đang soạn/gen thì BE trả 409 busy.
  const doApproval = async (kind: 'approve' | 'unapprove') => {
    setSending(kind);
    try {
      if (kind === 'approve') { const r = await filmApi.approveStoryboard(ev.id, noAutoPrompt ? { autoAuthorPrompt: false } : undefined); message.success('Đã duyệt storyboard — từ nay được đính vào video.'); onApproved?.(r?.autoPrompt); }
      else { await filmApi.unapproveStoryboard(ev.id); message.success('Đã bỏ duyệt storyboard'); onApproved?.(undefined); }
      await ctx.onReload();
    } catch (e: any) {
      fail(e, kind === 'approve' ? 'Duyệt storyboard' : 'Bỏ duyệt storyboard', storyboardApprovalErrorText(e, kind === 'approve' ? 'Duyệt storyboard' : 'Bỏ duyệt storyboard'));
      // STALE/NO_IMAGE: hiển thị đã lỗi thời (ai đó vừa sửa/gen) ⇒ nạp lại để nút khoá đúng.
      const code = e?.response?.data?.code;
      if (code === 'STALE' || code === 'NO_IMAGE' || code === 'NOT_FOUND') await ctx.onReload();
    } finally { setSending(null); }
  };

  const doSave = async () => {
    if (!sb) return;
    if (!validation.ok) { message.error(validation.errors[0]); return; }
    setSending('save');
    try {
      await filmApi.putStoryboardFrames(ev.id, sb.framesVersion, framesPayload(draft));
      message.success('Đã lưu khung');
      await ctx.onReload();
    } catch (e: any) {
      const text = storyboardErrorText(e, 'Lưu khung');
      if (isFramesChanged(e)) { await ctx.onReload(); message.warning({ content: text, duration: 10 }); onActionError?.(workflowActionError(e, 'Lưu khung')); }
      else fail(e, 'Lưu khung', text);
      const code = e?.response?.data?.code;
      if (code === 'NO_COMPOSITION' || code === 'NOT_FOUND') await ctx.onReload();
    } finally { setSending(null); }
  };

  // ───────── nút ─────────
  const composeLabel = sb ? 'AI soạn lại' : 'AI soạn';
  /** Khung 4 bước (ẩn giá) có dòng tiền ⇒ «AI soạn» lần đầu cũng hỏi xác nhận. */
  const costConfirm = !!hideCost && !!composeCostNote;
  const composeBtn = (
    <Button size="small" loading={sending === 'compose'} disabled={running || !ctx.loaded || !hasShots || policy?.compose?.enabled === false}
      onClick={cc.needed || costConfirm ? undefined : () => doCompose(false)}>{composeLabel}</Button>
  );
  const composeNode = cc.needed || costConfirm ? (
    <Popconfirm title={cc.needed ? 'AI soạn lại khung?' : 'AI soạn khung?'} okText={cc.needed ? 'Soạn lại' : 'Soạn'} cancelText="Huỷ" disabled={running || !hasShots} onConfirm={() => doCompose(cc.confirmReplace)}
      description={<div style={{ maxWidth: 320 }}>{cc.lines.map((l) => <div key={l} style={{ marginBottom: 4 }}>{l}</div>)}{!hideCost && <div style={{ color: '#888' }}>Một lượt AI chữ (vài cent), không gen ảnh.</div>}{hideCost && composeCostNote && <div data-testid="sb-compose-cost"><b>{composeCostNote}</b></div>}</div>}>
      {composeBtn}
    </Popconfirm>
  ) : composeBtn;
  const composeWithTip = !hasShots
    ? <Tooltip title="Video chưa có shot — dựng camera/shot trước rồi mới soạn khung được."><span>{composeNode}</span></Tooltip>
    : composeNode;

  const genBtn = (
    <Button size="small" type="primary" loading={sending === 'generate'} disabled={!genGate.ok}>
      {genButtonLabel({ hasImage: hasSheet, noun: 'tấm', cost, hideCost })}
    </Button>
  );
  const genConfirm = hideCost ? confirmContent('GEN_IMAGE', 'SB') : null;
  const genNode = (
    <Popconfirm title={genConfirm ? genConfirm.title : `${hasSheet ? 'Gen lại' : 'Gen'} tấm storyboard?`} okText={genConfirm ? genConfirm.okText : (hasSheet ? 'Gen lại' : 'Gen')} cancelText={genConfirm ? genConfirm.cancelText : 'Thôi'} disabled={!genGate.ok} onConfirm={doGenerate}
      description={genConfirm
        ? <div style={{ maxWidth: 320 }}>{genConfirm.body}{genCostNote && <div data-testid="sb-gen-cost" style={{ marginTop: 4 }}><b>{genCostNote}</b></div>}</div>
        : <div style={{ maxWidth: 300 }}>Giá ước <b>{cost}</b> mỗi tấm (một ảnh Sunburst 9:16, đo thật ~$0,21–0,22).{hasSheet ? ' Tấm đang dùng sẽ bị thay.' : ''}</div>}>
      {genBtn}
    </Popconfirm>
  );

  const approvalButtons = approval && (
    <>
      {approval.canUnapprove && (policy?.unapprove?.visible ?? true) && (
        <Tooltip title="Gỡ duyệt: tấm này thôi được đính vào video.">
          <Button size="small" loading={sending === 'unapprove'} disabled={running || sending !== null || policy?.unapprove?.enabled === false} onClick={() => doApproval('unapprove')} data-testid="sb-unapprove">Bỏ duyệt</Button>
        </Tooltip>
      )}
      {approval.approve && (policy?.approve?.visible ?? true) && (
        <Tooltip title={approval.approve.ok ? 'Chỉ bản đã duyệt mới vào video.' : approval.approve.reason}>
          <span><Button size="small" type={approval.approve.ok ? 'primary' : 'default'} ghost={approval.approve.ok} loading={sending === 'approve'} disabled={!approval.approve.ok || running || sending !== null || dirty || policy?.approve?.enabled === false} onClick={() => doApproval('approve')} data-testid="sb-approve">{approval.approve.label}</Button></span>
        </Tooltip>
      )}
    </>
  );

  // ───────── bảng khung ─────────
  const setCell = (frameNo: number, patch: Parameters<typeof patchFrame>[2]) => setDraft((d) => patchFrame(d, frameNo, patch));
  const firstOfGroup = (f: StoryboardFrame) => !draft.some((g) => g.groupNo === f.groupNo && g.frameNo < f.frameNo);
  const mapSelectOptions = (f: StoryboardFrame) => {
    if (!ctx.pos) return f.positionMapId ? [{ value: f.positionMapId, label: `(setup ${f.positionMapId.slice(0, 6)}…)` }] : [];
    return mapOptionsFor(f, ev.id, ctx.pos.links, ctx.pos.maps).map((o) => ({ value: o.value, label: o.label, disabled: !o.eligible }));
  };

  const frameTable = sb && (
    <div style={{ overflowX: 'auto' }}>
      <table data-testid="sb-frames" style={{ width: '100%', minWidth: 1300, borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#fafafa' }}>
            <th style={{ ...TH, width: 32 }}>#</th>
            <th style={{ ...TH, width: 62 }}>Nhóm</th>
            <th style={{ ...TH, width: 128 }}>Thời gian (s)</th>
            <th style={{ ...TH, width: 150 }}>Cỡ cảnh</th>
            <th style={{ ...TH, width: 150 }}>Góc</th>
            <th style={{ ...TH, width: 160 }}>Máy</th>
            <th style={{ ...TH, width: 150 }}>Ai</th>
            <th style={TH}>Hành động</th>
            <th style={{ ...TH, width: 170 }}>Sơ đồ</th>
          </tr>
        </thead>
        <tbody>
          {[...draft].sort((a, b) => a.frameNo - b.frameNo).map((f) => {
            const first = firstOfGroup(f);
            const actionLen = (f.action || '').length;
            return (
              <tr key={f.frameNo} data-testid={`sb-row-${f.frameNo}`} style={{ borderTop: first ? '2px solid #d9d9d9' : '1px solid #f0f0f0' }}>
                <td style={{ ...TD, fontWeight: 600 }}>{f.frameNo}</td>
                <td style={TD}>
                  <Select size="small" style={{ width: 54 }} value={f.groupNo} options={GROUP_OPTIONS} disabled={running} onChange={(v) => setDraft((d) => setFrameGroup(d, f.frameNo, v))} />
                </td>
                <td style={TD}>
                  {first ? (
                    <Tooltip title="Giờ là của cả NHÓM: đổi ở đây áp cho mọi khung cùng nhóm.">
                      <Space size={2}>
                        <InputNumber size="small" style={{ width: 54 }} min={0} step={0.5} controls={false} value={f.startS} disabled={running}
                          onChange={(v) => { if (typeof v === 'number') setDraft((d) => setGroupTime(d, f.groupNo, v, f.endS)); }} />
                        –
                        <InputNumber size="small" style={{ width: 54 }} min={0} step={0.5} controls={false} value={f.endS} disabled={running}
                          onChange={(v) => { if (typeof v === 'number') setDraft((d) => setGroupTime(d, f.groupNo, f.startS, v)); }} />
                      </Space>
                    </Tooltip>
                  ) : <span style={{ color: '#aaa' }}>↑ cùng nhóm ({fmtS(f.startS)}–{fmtS(f.endS)})</span>}
                </td>
                <td style={TD}><Select size="small" style={{ width: '100%' }} value={f.shotSize} options={toOptions(SHOT_SIZE_OPTIONS)} disabled={running} onChange={(v) => setCell(f.frameNo, { shotSize: v })} /></td>
                <td style={TD}><Select size="small" style={{ width: '100%' }} value={f.angle} options={toOptions(ANGLE_OPTIONS)} disabled={running} onChange={(v) => setCell(f.frameNo, { angle: v })} /></td>
                <td style={TD}><Select size="small" style={{ width: '100%' }} value={f.cameraMove} options={toOptions(MOVE_OPTIONS)} disabled={running} onChange={(v) => setCell(f.frameNo, { cameraMove: v })} /></td>
                <td style={TD}>
                  <Input.TextArea size="small" autoSize={{ minRows: 1, maxRows: 6 }} value={f.who} disabled={running} status={(f.who || '').length > WHO_MAX ? 'error' : undefined}
                    placeholder="ai có mặt" onChange={(e) => setCell(f.frameNo, { who: e.target.value })} />
                </td>
                <td style={TD}>
                  <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: 12 }} value={f.action} disabled={running} status={!(f.action || '').trim() || actionLen > ACTION_MAX ? 'error' : undefined}
                    placeholder="một câu hình ảnh cụ thể" onChange={(e) => setCell(f.frameNo, { action: e.target.value })} />
                  <div style={{ fontSize: 10, color: actionLen > ACTION_MAX ? '#cf1322' : '#aaa', textAlign: 'right' }}>{actionLen}/{ACTION_MAX}</div>
                </td>
                <td style={TD}>
                  <Select size="small" allowClear style={{ width: '100%' }} placeholder="— không —" value={f.positionMapId || undefined} options={mapSelectOptions(f)} disabled={running}
                    notFoundContent="Chưa có sơ đồ nào gán cho giai đoạn của khung này" onChange={(v) => setCell(f.frameNo, { positionMapId: v || null })} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // ───────── bố cục ─────────
  const sheetBox = (
    <div style={{ width: 188, flex: '0 0 188px' }}>
      <div style={{ width: '100%', aspectRatio: '9 / 16', background: '#0b0f19', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {hasSheet
          ? <Image src={sb!.sheetUrl!} alt="Storyboard" width="100%" height="100%" style={{ objectFit: 'contain', opacity: sb!.staleness === 'FRESH' ? 1 : 0.75 }} />
          : <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: 12 }}>{sb ? 'Chưa có tấm ảnh' : 'Chưa có storyboard'}</div>}
        {job?.phase === 'storyboard-generate' && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', gap: 8, fontSize: 12 }}><Spin size="small" /> Đang gen tấm…</div>}
      </div>
      {hasSheet && (
        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
          {[sb!.sheetSize, spent].filter(Boolean).join(' · ')}{sb!.sheetRawUrl ? <>{sb!.sheetSize || spent ? ' · ' : ''}<a href={sb!.sheetRawUrl} target="_blank" rel="noreferrer">ảnh gốc</a></> : null}
        </div>
      )}
    </div>
  );

  const border = job ? '#91caff' : failure ? '#ff7875' : sb?.staleness === 'INPUT_CHANGED' ? '#ffc069' : '#e8e8e8';

  return (
    <div data-testid={`sb-block-${ev.id}`} style={{ marginTop: 8, border: `1px solid ${border}`, borderRadius: 6, background: '#fff' }}>
      <div style={{ padding: '6px 10px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', background: '#fafafa', borderRadius: '6px 6px 0 0' }}>
        <b style={{ fontSize: 12 }}>🎞 Storyboard</b>
        {!ctx.loaded && <Spin size="small" />}
        {ctx.loaded && tags.map((t) => (
          <Tooltip key={t.key} title={t.tip ? <span style={{ whiteSpace: 'pre-line' }}>{t.tip}</span> : undefined}>
            <Tag color={t.color} style={{ margin: 0 }} data-testid={`sb-tag-${t.key}`}>{t.key === 'running' && <Spin size="small" style={{ marginRight: 4 }} />}{t.text}</Tag>
          </Tooltip>
        ))}
        {ctx.loaded && approval && <Tooltip title={approval.tag.tip}><Tag color={approval.tag.color} style={{ margin: 0 }} data-testid="sb-tag-approval">{approval.tag.text}</Tag></Tooltip>}
        {sb && <span style={{ fontSize: 11, color: '#888' }}>{sb.frames.length} khung · {sb.groups.length} nhóm{typeof ev.genDurationS === 'number' ? ` · ${fmtS(ev.genDurationS)}s` : ''}</span>}
        <span style={{ flex: 1 }} />
        {ctx.loaded && approvalButtons}
        {ctx.loaded && composeWithTip}
        {ctx.loaded && genGate.visible && (genGate.ok ? genNode : <Tooltip title={genGate.reason}><span>{genNode}</span></Tooltip>)}
        {!alwaysOpen && <Button size="small" type="link" onClick={toggle}>{open ? 'Thu gọn' : 'Mở'}</Button>}
      </div>

      {ctx.loadError && (
        <Alert type="error" showIcon style={{ margin: '6px 10px' }} message={ctx.loadError}
          action={<Button size="small" onClick={() => { ctx.onReload(); }} loading={ctx.loading}>Tải lại</Button>} />
      )}
      {failure && (
        <Alert type="error" showIcon closable style={{ margin: '6px 10px' }} onClose={() => setDismissed((d) => [...d, failure.id])}
          message={`${storyboardJobName(failure.phase)} thất bại — ${failure.minutesAgo}′ trước`}
          description={<span style={{ fontSize: 12 }}>{failure.note}</span>} />
      )}
      {sb && (blocking.length > 0 || notes.length > 0) && (
        <div data-testid="sb-warnings" style={{ margin: '6px 10px' }}>
          {blocking.length > 0 && <Alert type="error" showIcon style={{ marginBottom: 4 }} message="Chưa gen tấm được" description={<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{refsWarningLines(blocking).map((w) => <li key={w}>{w}</li>)}</ul>} />}
          {notes.length > 0 && <Alert type="warning" showIcon message="Lưu ý ảnh tham chiếu trước khi gen" description={<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{refsWarningLines(notes).map((w) => <li key={w}>{w}</li>)}</ul>} />}
        </div>
      )}

      {open && (
        <div style={{ padding: '8px 10px', display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {sheetBox}
          <div style={{ flex: '1 1 480px', minWidth: 0 }}>
            {!ctx.loaded && <div style={{ padding: 16, textAlign: 'center' }}><Spin /></div>}
            {ctx.loaded && !sb && (
              <Alert type="info" showIcon message="Video này chưa có storyboard"
                description={hasShots ? 'Bấm "AI soạn": AI dựng danh sách khung + nhóm thời gian từ shot, giai đoạn và sơ đồ định vị. Sau đó sửa tay được, rồi mới gen tấm (bước riêng, tốn tiền).' : 'Video chưa có shot — dựng camera/shot trước rồi mới soạn khung được.'} />
            )}
            {sb && sb.staleness === 'INPUT_CHANGED' && hasSheet && (
              <Alert type="warning" showIcon style={{ marginBottom: 6 }} message="Tấm này đã cũ" description="Chữ khung, ảnh sơ đồ / sheet nhân vật / bối cảnh hoặc cỡ ảnh đã đổi sau lần gen. Tấm cũ vẫn xem được; bấm Gen lại để thay bản mới." />
            )}
            {drift.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 6 }} message="Khung lệch nguồn"
                description={<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{drift.map((l) => <li key={l}>{l}</li>)}</ul>} />
            )}
            {frameTable}
            {sb && (
              <>
                {dirty && validation.errors.length > 0 && (
                  <Alert type="error" showIcon style={{ marginTop: 6 }} message="Chưa lưu được" data-testid="sb-errors"
                    description={<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{validation.errors.map((x) => <li key={x}>{x}</li>)}</ul>} />
                )}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Tooltip title={!saveGate.ok ? saveGate.reason : (!validation.ok ? 'Sửa lỗi ở trên trước khi lưu.' : undefined)}>
                    <span><Button size="small" type="primary" loading={sending === 'save'} disabled={!saveGate.ok || !validation.ok} onClick={doSave}>Lưu khung</Button></span>
                  </Tooltip>
                  <Button size="small" disabled={!dirty || running} onClick={() => setDraft(cloneFrames(sb.frames))}>Hoàn tác</Button>
                  <Button size="small" type="text" onClick={() => { ctx.onReload(); }} loading={ctx.loading}>⟳</Button>
                  {dirty && <span style={{ fontSize: 12, color: '#d46b08' }}>Có chỉnh sửa chưa lưu — nút Gen bị khoá tới khi lưu.</span>}
                </div>
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>Chú thích in dưới tấm (code vẽ){dirty ? <span style={{ fontWeight: 400, color: '#d46b08' }}> — cập nhật sau khi lưu</span> : null}</div>
                  {sb.groups.map((g) => <div key={g.groupNo} data-testid={`sb-caption-${g.groupNo}`} style={{ color: '#555' }}>Nhóm {g.groupNo}: {g.caption}</div>)}
                </div>
                <details style={{ marginTop: 8, fontSize: 12 }}>
                  <summary style={{ cursor: 'pointer' }}>Prompt tấm{chipPrompt ? '' : ` & ảnh tham chiếu (${(sb.refsPreview || []).length})`}</summary>
                  {chipPrompt ? (
                    <div data-testid="sb-prompt-chip" style={{ margin: '4px 0' }}>
                      <PromptChipEditor value={chipView.text} extraChoices={chipView.choices} readOnly emptyText="(chưa dựng được prompt — xem cảnh báo ở trên)" onSave={() => undefined} />
                    </div>
                  ) : <pre data-testid="sb-prompt" style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: '4px 0', maxHeight: 220, overflow: 'auto', background: '#fafafa', padding: 6, borderRadius: 4 }}>{sb.renderedPrompt || '(chưa dựng được prompt — xem cảnh báo ở trên)'}</pre>}
                  {(chipPrompt ? [] : (sb.refsPreview || [])).map((r) => (
                    <div key={r.slot} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                      <b style={{ minWidth: 18 }}>{r.slot}</b>
                      <Image src={r.url} width={36} height={36} style={{ objectFit: 'cover', borderRadius: 3 }} />
                      <span style={{ flex: 1, color: '#555' }}>{r.label}</span>
                    </div>
                  ))}
                </details>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryboardBlock;
